import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { SeverityChip } from 'components/shared/SeverityChip';
import { VideoWallAlert, AlertSeverity, AlertType } from '../../types/canvision.types';

interface AlertCardProps {
  alert: VideoWallAlert;
  onView: (alert: VideoWallAlert) => void;
  onDismiss: (alertId: number) => void;
  compact?: boolean;
}

// Border colors by severity using MUI theme tokens
const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  critical: 'error.main',
  high: 'warning.main',
  medium: 'warning.light',
  low: 'primary.main',
};

// Palette key for background alpha
const SEVERITY_PALETTE: Record<AlertSeverity, string> = {
  critical: 'error',
  high: 'warning',
  medium: 'warning',
  low: 'primary',
};

// Icons by alert type
function AlertTypeIcon({ type }: { type: AlertType }) {
  switch (type) {
    case 'camera_offline':
      return <VideocamOffIcon fontSize="small" />;
    case 'motion':
      return <DirectionsRunIcon fontSize="small" />;
    case 'incident':
    default:
      return <WarningAmberIcon fontSize="small" />;
  }
}

// Severity icon
function SeverityIcon({ severity }: { severity: AlertSeverity }) {
  switch (severity) {
    case 'critical':
    case 'high':
      return <ErrorIcon fontSize="small" sx={{ color: SEVERITY_COLORS[severity] }} />;
    case 'medium':
      return <WarningAmberIcon fontSize="small" sx={{ color: SEVERITY_COLORS[severity] }} />;
    case 'low':
    default:
      return <InfoIcon fontSize="small" sx={{ color: SEVERITY_COLORS[severity] }} />;
  }
}

// Format relative time
function formatRelativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const seconds = Math.floor(diff / 1000);

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function AlertCard({ alert, onView, onDismiss, compact = false }: AlertCardProps) {
  const severityColor = SEVERITY_COLORS[alert.severity];

  return (
    <Card
      sx={{
        width: compact ? 280 : 320,
        borderLeft: 4,
        borderColor: severityColor,
        bgcolor: 'action.hover',
        backdropFilter: 'blur(8px)',
        boxShadow: 3,
      }}
    >
      <CardContent sx={{ pb: 1, pt: compact ? 1.5 : 2 }}>
        {/* Header with close button */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <SeverityIcon severity={alert.severity} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              noWrap
              sx={{ color: severityColor }}
            >
              {alert.title}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => onDismiss(alert.id)}
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Message */}
        {!compact && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, mb: 1 }}
          >
            {alert.message}
          </Typography>
        )}

        {/* Camera and location info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <AlertTypeIcon type={alert.type} />
          <Typography variant="caption" color="text.secondary" noWrap>
            {alert.cameraName} - {alert.siteName}
          </Typography>
        </Box>

        {/* Timestamp and severity chip */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {formatRelativeTime(alert.createdAt)}
          </Typography>
          <SeverityChip severity={alert.severity} size="small" />
        </Box>

        {/* Thumbnail if available */}
        {alert.thumbnailUrl && !compact && (
          <Box
            component="img"
            src={alert.thumbnailUrl}
            alt="Alert thumbnail"
            sx={{
              width: '100%',
              height: 80,
              objectFit: 'cover',
              borderRadius: 1,
              mt: 1,
            }}
          />
        )}
      </CardContent>

      <CardActions sx={{ pt: 0, px: 2, pb: 1.5 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => onView(alert)}
          sx={{ flex: 1 }}
        >
          View
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onDismiss(alert.id)}
          sx={{ flex: 1 }}
        >
          Dismiss
        </Button>
      </CardActions>
    </Card>
  );
}

export default AlertCard;
